import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useEffect } from 'react';
import { UrlMetadataResult } from 'src/@types/url';
import { useUrlMetadataQuery } from '../hooks/useUrlMetadataQuery';

const Link = styled('a')(() => ({
  textDecoration: 'none',
  overflow: 'hidden',
  maxWidth: '100%',

  '&:hover': {
    textDecoration: 'none',
  },
}));

const TypographyStyled = styled(Typography)(() => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  WebkitLineClamp: 2,
  width: '100%',
  marginTop: 0,
}));

type UrlMetadataProps<D extends object = Record<string, string>> = Omit<
  React.HTMLProps<HTMLAnchorElement>,
  'as'
> & {
  url: string;
  metadata?: D;
  onMetadataChange?: (metadata: D) => void;
  preferFavicon?: boolean;
};

export const UrlMetadata = ({
  url,
  onMetadataChange,
  metadata,
  preferFavicon,
  ...aProps
}: UrlMetadataProps<UrlMetadataResult>) => {
  const { data, isRefetching } = useUrlMetadataQuery(url, metadata);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (data && !metadata && onMetadataChange) {
      onMetadataChange(data);
    }
  }, [data, onMetadataChange]);

  const coverImage = preferFavicon ? data?.favicon : data?.coverImage;

  return (
    <Link {...aProps} href={url} target="_blank">
      {isRefetching && (
        <Box
          sx={{
            p: 4,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      {data && (
        <Stack gap={2} direction="row" maxWidth={1}>
          {coverImage && (
            <Box
              sx={{
                height: '100%',
              }}
            >
              <img
                src={coverImage}
                alt={data?.title || 'website cover image'}
                onError={(e) => {
                  // @ts-ignore
                  e.target.src = url + coverImage || '';
                }}
                style={{
                  objectFit: 'cover',
                  ...(preferFavicon
                    ? {
                        minWidth: '48px',
                        maxWidth: '48px',
                        aspectRatio: '1/1',
                      }
                    : {
                        height: '100%',
                        aspectRatio: '16/9',
                      }),
                }}
              />
            </Box>
          )}
          <Box flexGrow={1} overflow="hidden">
            <TypographyStyled variant="subtitle1" color="text.primary" as="p">
              {data?.title}
            </TypographyStyled>
            {data?.description && (
              <TypographyStyled
                gutterBottom
                variant="body2"
                color="text.secondary"
              >
                {data?.description}
              </TypographyStyled>
            )}
            <TypographyStyled
              variant="body2"
              color="text.secondary"
              sx={{
                WebkitLineClamp: 1,
              }}
            >
              {data.canonical || url}
            </TypographyStyled>
          </Box>
        </Stack>
      )}
    </Link>
  );
};
